import * as React from "react";

import { v4 as uuidv4 } from "uuid";

import { trackEvent } from "@needl-ai/common/analytics";
import { ASKNEO_SERVICE_STORAGE_KEY } from "@needl-ai/common/hooks/useFetchAskneoServices";

import type { Message } from "./ChatContextTypes";
import type { ChatState } from "./ChatState";
import type { FileItem } from "./FileUploadContext";
import type { AskNeedlTools } from "@/types/api";

// Simplified actions interface - session management removed
export interface ChatActionsParams {
  state: ChatState;
  setters: {
    setSessionId: (id: string) => void;
    setMessages: (
      messages: Message[] | ((prev: Message[]) => Message[])
    ) => void;
    setInput: (input: string) => void;
    setTools: (tools: AskNeedlTools | null) => void;
    setReportType: (reportType: string | null) => void;
  };
  rmChat: {
    askRMChat: (params: {
      prompt: string;
      session_id: string;
      tools?: AskNeedlTools;
      attachment_feed_id?: string;
      report_type?: string;
      is_async?: boolean;
      voice_count?: number;
    }) => Promise<unknown>;
    clearEventData: () => void;
  };
  getFeedId: () => string | null; // Add function to get current feed ID
  getAttachments: () => FileItem[]; // Add function to get current attachments
  clearAttachmentsOnly: () => void; // Add function to clear attachments but keep feed_id
  saveSession?: (
    messages: Message[],
    options?: { skipSessionInvalidation?: boolean }
  ) => Promise<void>;
  setLoading?: (loading: boolean) => void;
  voiceCountRef: React.MutableRefObject<number>;
}

export const useChatActions = ({
  state,
  setters,
  rmChat,
  getFeedId,
  getAttachments,
  clearAttachmentsOnly,
  saveSession,
  setLoading,
  voiceCountRef,
}: ChatActionsParams) => {
  const { sessionId, tools, reportType } = state;

  const { setSessionId, setMessages, setInput, setTools, setReportType } =
    setters;

  const { askRMChat, clearEventData } = rmChat;

  // Handler for sending a new message
  const sendMessage = React.useCallback(
    async (message: string) => {
      if (!message.trim()) return;

      // Ensure we have a sessionId
      let currentSessionId = sessionId;

      if (!currentSessionId) {
        console.warn(
          "sendMessage called without sessionId - creating new session"
        );
        currentSessionId = uuidv4();
        setSessionId(currentSessionId);
      }

      // Get current attachments
      const currentAttachments = getAttachments();
      const currentFeedId = getFeedId();

      // Create a new user message with attachments
      const userMessage: Message = {
        id: uuidv4(),
        text: message,
        sender: "user",
        timestamp: new Date(),
        message_status: "FULL_ANSWER_GENERATED",
        // Include attachments if they exist and are completed
        attachments: currentAttachments.filter(
          (att) => att.status === "COMPLETED" || att.status === "UPLOADED"
        ),
        // Include feed ID if attachments exist
        ...(currentAttachments.length > 0 && currentFeedId
          ? { attachmentsFeedId: currentFeedId }
          : {}),
      };

      // Add the user message to the conversation
      setMessages((prev) => [...prev, userMessage]);

      // Clear attachments (but keep feed_id for future uploads)
      clearAttachmentsOnly();

      // Clear the input
      setInput("");

      const chatParams: {
        prompt: string;
        session_id: string;
        tools?: AskNeedlTools;
        attachment_feed_id?: string;
        report_type?: string;
        is_async?: boolean;
        voice_count?: number;
        askneo_service_id?: string;
      } = {
        prompt: message,
        session_id: currentSessionId,
      };

      if (tools) {
        chatParams.tools = tools;
      }

      const feedId = getFeedId();
      if (feedId) {
        chatParams.attachment_feed_id = feedId;
      }

      const capturedVoiceCount = voiceCountRef.current;
      if (capturedVoiceCount > 0) {
        chatParams.voice_count = capturedVoiceCount;
      }

      if (reportType) {
        chatParams.report_type = reportType;
      }

      const storedServiceId = localStorage.getItem(ASKNEO_SERVICE_STORAGE_KEY);
      if (storedServiceId) {
        chatParams.askneo_service_id = storedServiceId;
      }

      chatParams.is_async = true;

      trackEvent("query_submitted", {
        session_id: currentSessionId,
        input_method: capturedVoiceCount > 0 ? "voice" : "send_btn",
        has_attachment: currentAttachments.length > 0,
        attachment_count: currentAttachments.length,
        tools_enabled: tools
          ? JSON.stringify(
              tools
                .filter((t) =>
                  t.type === "toggle"
                    ? t.enabled
                    : t.type === "group"
                      ? (t as { sources: { enabled: boolean }[] }).sources.some(
                          (s) => s.enabled
                        )
                      : false
                )
                .map((t) => t.id)
            )
          : "[]",
        query: message,
      });

      setLoading?.(true);
      try {
        await saveSession?.([...state.messages, userMessage], {
          skipSessionInvalidation: true,
        });
      } catch {
        setLoading?.(false);
        return;
      }

      await askRMChat(chatParams);
      voiceCountRef.current = 0;
    },
    [
      sessionId,
      tools,
      reportType,
      setSessionId,
      setMessages,
      setInput,
      askRMChat,
      getFeedId,
      getAttachments,
      clearAttachmentsOnly,
      saveSession,
      state.messages,
      voiceCountRef,
    ]
  );

  // Update the input field
  const updateInput = React.useCallback(
    (value: string) => {
      setInput(value);
    },
    [setInput]
  );

  // Clear the conversation and start a new session
  const clearConversation = React.useCallback(() => {
    clearEventData();
    setMessages([]);
    setInput("");
    voiceCountRef.current = 0;

    const newSessionId = uuidv4();
    setSessionId(newSessionId);
  }, [clearEventData, setMessages, setInput, setSessionId, voiceCountRef]);

  // Handle when a user selects a choice option
  const handleChoiceSelect = React.useCallback(
    (choice: string) => {
      sendMessage(choice);
    },
    [sendMessage]
  );

  // Function to stop ongoing request
  const stopRequest = React.useCallback(() => {
    clearEventData();
  }, [clearEventData]);

  // Function to hide/unhide a message (and its corresponding question/answer pair)
  const hideMessage = React.useCallback(
    (messageId: string) => {
      setMessages((prevMessages) => {
        // Find the message index
        const messageIndex = prevMessages.findIndex((m) => m.id === messageId);
        if (messageIndex === -1) return prevMessages;

        const message = prevMessages[messageIndex];
        const newIsHidden = !message.isHidden;

        // Create new messages array with updated isHidden status
        const newMessages = prevMessages.map((msg, idx) => {
          if (message.sender === "ai" && msg.id === messageId) {
            return { ...msg, isHidden: newIsHidden };
          }
          if (
            message.sender === "ai" &&
            idx === messageIndex - 1 &&
            msg.sender === "user"
          ) {
            return { ...msg, isHidden: newIsHidden };
          }
          return msg;
        });

        saveSession?.(newMessages);

        return newMessages;
      });
    },
    [setMessages, saveSession]
  );

  return {
    sendMessage,
    updateInput,
    clearConversation,
    handleChoiceSelect,
    setSessionId,
    setMessages,
    setTools,
    setReportType,
    stopRequest,
    hideMessage,
  };
};
